(() => {
  'use strict';

  const root = document.documentElement;
  const hero = document.querySelector('.hero');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const causticHosts = Array.from(document.querySelectorAll([
    '.featured-media',
    '.gallery-card:nth-child(2)',
    '.product-gallery-item:nth-child(3)'
  ].join(',')));

  const causticSettings = [
    { top: '31%', left: '62%', angle: '-13deg', duration: '17s', delay: '-4s', opacity: '.19' },
    { top: '56%', left: '42%', angle: '9deg', duration: '21s', delay: '-13s', opacity: '.16' },
    { top: '24%', left: '69%', angle: '-7deg', duration: '19s', delay: '-9s', opacity: '.17' }
  ];

  causticHosts.forEach((host, index) => {
    if (host.querySelector('.prism-caustic')) return;

    const settings = causticSettings[index % causticSettings.length];
    const caustic = document.createElement('span');

    host.classList.add('prism-caustic-host');
    caustic.className = 'prism-caustic';
    caustic.setAttribute('aria-hidden', 'true');
    caustic.style.setProperty('--prism-caustic-top', settings.top);
    caustic.style.setProperty('--prism-caustic-left', settings.left);
    caustic.style.setProperty('--prism-caustic-angle', settings.angle);
    caustic.style.setProperty('--prism-caustic-duration', settings.duration);
    caustic.style.setProperty('--prism-caustic-delay', settings.delay);
    caustic.style.setProperty('--prism-caustic-opacity', settings.opacity);
    host.appendChild(caustic);
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-prism-visible', entry.isIntersecting);
      });
    }, { rootMargin: '120px 0px', threshold: 0.05 });

    causticHosts.forEach((host) => observer.observe(host));
  } else {
    causticHosts.forEach((host) => host.classList.add('is-prism-visible'));
  }

  if (!hero) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + ((to - from) * amount);
  const current = { x: 78, y: 27, angle: -11, ambientX: 0, ambientY: 0 };
  const target = { ...current };
  let pointerX = 0.5;
  let pointerY = 0.42;
  let frame = 0;
  let targetFrame = 0;
  let heroVisible = !('IntersectionObserver' in window);
  let pageVisible = !document.hidden;

  const canAnimate = () => heroVisible && pageVisible && !reducedMotion.matches;

  const cancelFrames = () => {
    if (frame) window.cancelAnimationFrame(frame);
    if (targetFrame) window.cancelAnimationFrame(targetFrame);
    frame = 0;
    targetFrame = 0;
  };

  const writeFrame = () => {
    if (!canAnimate()) {
      frame = 0;
      return;
    }

    current.x = lerp(current.x, target.x, 0.095);
    current.y = lerp(current.y, target.y, 0.095);
    current.angle = lerp(current.angle, target.angle, 0.085);
    current.ambientX = lerp(current.ambientX, target.ambientX, 0.08);
    current.ambientY = lerp(current.ambientY, target.ambientY, 0.08);

    hero.style.setProperty('--prism-x', `${current.x.toFixed(2)}%`);
    hero.style.setProperty('--prism-y', `${current.y.toFixed(2)}%`);
    hero.style.setProperty('--prism-angle', `${current.angle.toFixed(2)}deg`);
    root.style.setProperty('--prism-ambient-x', `${current.ambientX.toFixed(2)}px`);
    root.style.setProperty('--prism-ambient-y', `${current.ambientY.toFixed(2)}px`);

    const remaining = Math.abs(target.x - current.x)
      + Math.abs(target.y - current.y)
      + Math.abs(target.angle - current.angle)
      + Math.abs(target.ambientX - current.ambientX)
      + Math.abs(target.ambientY - current.ambientY);

    if (remaining > 0.035) {
      frame = window.requestAnimationFrame(writeFrame);
    } else {
      frame = 0;
    }
  };

  const requestWrite = () => {
    if (canAnimate() && !frame) frame = window.requestAnimationFrame(writeFrame);
  };

  const calculateTargets = () => {
    const viewportWidth = Math.max(window.innerWidth, 1);
    const viewportHeight = Math.max(window.innerHeight, 1);
    const mobile = viewportWidth < 900;
    const heroRect = hero.getBoundingClientRect();
    const heroProgress = clamp(-heroRect.top / Math.max(heroRect.height, 1), 0, 1);
    const documentRange = Math.max(document.documentElement.scrollHeight - viewportHeight, 1);
    const documentProgress = clamp(window.scrollY / documentRange, 0, 1);

    target.x = (mobile ? 71 : 78) + ((pointerX - 0.5) * (mobile ? 30 : 23));
    target.y = (mobile ? 25 : 27) + ((pointerY - 0.5) * (mobile ? 12 : 15)) + (heroProgress * 7);
    target.angle = -11 + ((pointerX - 0.5) * 10) + (heroProgress * 3.5);
    target.ambientX = (pointerX - 0.5) * (mobile ? 7 : 16);
    target.ambientY = ((documentProgress - 0.5) * (mobile ? 8 : 18));
    requestWrite();
  };

  const queueTargetUpdate = () => {
    if (!canAnimate() || targetFrame) return;

    targetFrame = window.requestAnimationFrame(() => {
      targetFrame = 0;
      calculateTargets();
    });
  };

  if (finePointer.matches) {
    window.addEventListener('pointermove', (event) => {
      pointerX = clamp(event.clientX / Math.max(window.innerWidth, 1), 0, 1);
      pointerY = clamp(event.clientY / Math.max(window.innerHeight, 1), 0, 1);
      queueTargetUpdate();
    }, { passive: true });
  }

  window.addEventListener('scroll', queueTargetUpdate, { passive: true });
  window.addEventListener('resize', queueTargetUpdate, { passive: true });

  if ('IntersectionObserver' in window) {
    const heroObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        heroVisible = entry.isIntersecting;
        hero.classList.toggle('is-prism-visible', heroVisible);

        if (canAnimate()) {
          queueTargetUpdate();
        } else {
          cancelFrames();
        }
      });
    }, { threshold: 0 });

    heroObserver.observe(hero);
  } else {
    hero.classList.add('is-prism-visible');
  }

  document.addEventListener('visibilitychange', () => {
    pageVisible = !document.hidden;
    if (canAnimate()) {
      queueTargetUpdate();
    } else {
      cancelFrames();
    }
  });

  if ('addEventListener' in reducedMotion) {
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches) {
        cancelFrames();
        hero.style.removeProperty('--prism-x');
        hero.style.removeProperty('--prism-y');
        hero.style.removeProperty('--prism-angle');
        root.style.removeProperty('--prism-ambient-x');
        root.style.removeProperty('--prism-ambient-y');
      } else {
        queueTargetUpdate();
      }
    });
  }

  if (canAnimate()) calculateTargets();
})();
